import { useCallback, useContext, useId, useMemo } from 'react'
import * as React from 'react'

import { buildAccommodationCancellationPolicies } from '@luxuryescapes/lib-refunds'
import FormatCurrency from 'components/Common/FormatCurrency'
import Clickable from 'components/Common/Clickable'
import HelpPane from 'components/Common/HelpPane'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import InlineIcon from 'components/utils/InlineIcon'
import ModalContext from 'contexts/ModalContext'
import HotelLoyaltyPointsEarnLabel from '../shared/HotelLoyaltyPointsEarnLabel'
import {
  getHotelLoyaltyProgramNameFromRateInclusions,
  isPropertyRateLoyaltyEligible
} from 'lib/hotels/hotelLoyaltyEligibility'
import { getPropertyRateCancellationDisplayText } from 'lib/property/getPropertyRateCancellationDisplayText'
import { getPropertyRateVariantPriceDifference } from 'lib/property/getPropertyRateDisplayPrice'

interface Props {
  rates: Array<App.PropertyRate>
  selectedRateId: string | undefined
  onChange: (rateId: string) => void
  propertyTimezone: string
  currency: string
  isLoyaltyProgramFeatureFlagEnabled: boolean
  sabreHotelsEnabled: boolean
}

function PropertyRateVariantSelector(props: Props) {
  const {
    rates,
    selectedRateId,
    onChange,
    propertyTimezone,
    currency,
    isLoyaltyProgramFeatureFlagEnabled,
    sabreHotelsEnabled,
  } = props

  const radioGroupName = useId()
  const showModal = useContext(ModalContext)
  const selectedRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }, [onChange])

  const handleShowCancellationDetails = useCallback((rate: App.PropertyRate, label: string) => {
    const { description: policyDescriptions } = buildAccommodationCancellationPolicies({
      fullRefundableUntil: rate.fullyRefundableUntil,
      cancellationPolicies: rate.cancellationPolicies.map(({
        start,
        end,
        amount,
        description,
        nonRefundable,
      }) => ({
        amount,
        percent: 0,
        description,
        start,
        end,
        nonRefundable,
      })),
    }, {
      timezone: propertyTimezone,
      currencyCode: currency,
      hideExcludesFlights: !rate.includesFlights,
    })

    showModal(<Modal title={label}>
      {policyDescriptions.map((line, index) => <BodyText variant="large" as="p" key={index}>{line}</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [currency, propertyTimezone, showModal])

  if (rates.length === 0) {
    return null
  }

  return (
    <RadioGroup>
      {rates.map((rate) => {
        const text = getPropertyRateCancellationDisplayText(rate, propertyTimezone)
        const isLoyaltyEligible = isPropertyRateLoyaltyEligible(rate, isLoyaltyProgramFeatureFlagEnabled, sabreHotelsEnabled)
        const loyaltyProgramName = getHotelLoyaltyProgramNameFromRateInclusions(rate.inclusions)
        const isSelected = rate.rateId === selectedRateId
        const priceDifference = getPropertyRateVariantPriceDifference(rate, selectedRate)

        return (
          <RadioInput
            key={rate.rateId}
            name={radioGroupName}
            value={rate.rateId}
            onChange={handleChange}
            checked={isSelected}
            labelAlign="start"
          >
            <Group direction="vertical" gap={4}>
              <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
                <BodyText
                  variant="medium"
                  weight={text.highlightPrimary ? 'bold' : undefined}
                  colour={text.highlightPrimary ? 'highlight-secondary' : undefined}
                  wrap="no-wrap"
                >
                  {text.primary}
                </BodyText>
                {text.secondary && (
                  <BodyText variant="medium" wrap="no-wrap">
                    {text.secondary}
                  </BodyText>
                )}
                {!isSelected && priceDifference > 0 && (
                  <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
                    +<FormatCurrency value={priceDifference} format="roundedDollar" />
                  </BodyText>
                )}
                {!isSelected && priceDifference < 0 && (
                  <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
                    <FormatCurrency value={priceDifference} format="roundedDollar" />
                  </BodyText>
                )}
                <Clickable
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    handleShowCancellationDetails(rate, text.primary)
                  }}
                >
                  <InlineIcon Icon={LineInfoCircleIcon} />
                </Clickable>
              </Group>
              {isLoyaltyEligible && (
                <HotelLoyaltyPointsEarnLabel loyaltyProgramName={loyaltyProgramName} />
              )}
            </Group>
          </RadioInput>
        )
      })}
    </RadioGroup>
  )
}

export default PropertyRateVariantSelector
